import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { safeStorage } from "@/shared/lib/storage/safe-storage";

/**
 * The active location's IANA timezone — the single source of truth for time
 * DISPLAY across the app.
 *
 * Timezone is modelled per LOCATION by the backend (there is no organization
 * timezone). `/api/settings/` serves each location's configured zone; a sync
 * hook publishes that map here, and the pages that own a location selection
 * publish which one is active.
 *
 * The location→zone map is persisted as a CACHE so a reload paints times in the
 * right zone immediately instead of flashing the viewer's zone until
 * `/api/settings/` answers. It is overwritten wholesale by every sync, and
 * saving a timezone in Settings invalidates that query, so a rehydrated value
 * can only be stale for the duration of one request.
 *
 * `activeLocationId` is NOT persisted — it is navigation state owned by the
 * pages, and restoring it would apply one page's location to another.
 */

/** The viewer's own zone — the fallback whenever no location zone is configured. */
export const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export interface TimezoneState {
  /** The location whose zone times are rendered in, or null when none is selected. */
  activeLocationId: number | null;
  /** Location id → configured IANA zone; null means the location has none set. */
  timezoneByLocation: Record<number, string | null>;
  setActiveLocation: (id: number | null) => void;
  setLocationTimezones: (map: Record<number, string | null>) => void;
}

export const useTimezoneStore = create<TimezoneState>()(
  persist(
    (set) => ({
      activeLocationId: null,
      timezoneByLocation: {},
      setActiveLocation: (id) => set({ activeLocationId: id }),
      setLocationTimezones: (map) => set({ timezoneByLocation: map }),
    }),
    {
      name: "maestro-location-timezones",
      storage: createJSONStorage(() => safeStorage),
      // Cache the API map only; the active location is per-navigation state.
      partialize: (state) => ({ timezoneByLocation: state.timezoneByLocation }),
    },
  ),
);

/**
 * The one resolution rule: the active location's configured zone, falling back
 * to the viewer's own zone when no location is active, the location is unknown,
 * or its timezone is unset (`null`).
 */
export const resolveTimezone = (state: TimezoneState): string =>
  (state.activeLocationId != null ? state.timezoneByLocation[state.activeLocationId] : null) ??
  BROWSER_TZ;
